import React from 'react';
import { Input, Button, Checkbox, DatePicker } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

interface QueryParamsProps {
  dateFrom: string;
  dateTo: string;
  invoiceFrom: string;
  invoiceTo: string;
  deptFrom: string;
  deptTo: string;
  vendorFrom: string;
  vendorTo: string;
  byVendor: boolean;
  byDept: boolean;
  byDoc: boolean;
  loading: boolean;
  onDateFromChange: (v: string) => void;
  onDateToChange: (v: string) => void;
  onInvoiceFromChange: (v: string) => void;
  onInvoiceToChange: (v: string) => void;
  onDeptFromChange: (v: string) => void;
  onDeptToChange: (v: string) => void;
  onVendorFromChange: (v: string) => void;
  onVendorToChange: (v: string) => void;
  onByVendorChange: (v: boolean) => void;
  onByDeptChange: (v: boolean) => void;
  onByDocChange: (v: boolean) => void;
  onQuery: () => void;
}

const QueryParams: React.FC<QueryParamsProps> = ({
  dateFrom, dateTo,
  invoiceFrom, invoiceTo,
  deptFrom, deptTo,
  vendorFrom, vendorTo,
  byVendor, byDept, byDoc,
  loading,
  onDateFromChange, onDateToChange,
  onInvoiceFromChange, onInvoiceToChange,
  onDeptFromChange, onDeptToChange,
  onVendorFromChange, onVendorToChange,
  onByVendorChange, onByDeptChange, onByDocChange,
  onQuery,
}) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
      {/* Row 1: Date range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 100 }}>請款日期[從]</span>
          <DatePicker
            value={dateFrom ? dayjs(dateFrom) : null}
            onChange={(_date, dateStr) => onDateFromChange(typeof dateStr === 'string' ? dateStr : '')}
            style={{ width: 140 }}
            placeholder="起始日期"
          />
          <span className="text-sm">[至]</span>
          <DatePicker
            value={dateTo ? dayjs(dateTo) : null}
            onChange={(_date, dateStr) => onDateToChange(typeof dateStr === 'string' ? dateStr : '')}
            style={{ width: 140 }}
            placeholder="結束日期"
          />
        </div>
      </div>

      {/* Row 2: Invoice number range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 100 }}>請款單號[從]</span>
          <Input
            value={invoiceFrom}
            onChange={(e) => onInvoiceFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 140 }}
            maxLength={6}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={invoiceTo}
            onChange={(e) => onInvoiceToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 140 }}
            maxLength={6}
          />
        </div>
      </div>

      {/* Row 3: Department range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 100 }}>部門代號[從]</span>
          <Input
            value={deptFrom}
            onChange={(e) => onDeptFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 140 }}
            maxLength={4}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={deptTo}
            onChange={(e) => onDeptToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 140 }}
            maxLength={4}
          />
        </div>
      </div>

      {/* Row 4: Vendor/Employee range */}
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium" style={{ width: 100 }}>廠商代號[從]</span>
          <Input
            value={vendorFrom}
            onChange={(e) => onVendorFromChange(e.target.value.toUpperCase())}
            placeholder="起"
            style={{ width: 140 }}
            maxLength={12}
          />
          <span className="text-sm">[至]</span>
          <Input
            value={vendorTo}
            onChange={(e) => onVendorToChange(e.target.value.toUpperCase())}
            placeholder="迄"
            style={{ width: 140 }}
            maxLength={12}
          />
        </div>
      </div>

      {/* Row 5: Print selection + Query button */}
      <div className="flex flex-wrap items-center gap-4">
        <span className="text-sm font-medium">印表選擇</span>
        <Checkbox checked={byVendor} onChange={(e) => onByVendorChange(e.target.checked)}>
          廠商別
        </Checkbox>
        <Checkbox checked={byDept} onChange={(e) => onByDeptChange(e.target.checked)}>
          部門別
        </Checkbox>
        <Checkbox checked={byDoc} onChange={(e) => onByDocChange(e.target.checked)}>
          單據別
        </Checkbox>
        <Button type="primary" icon={<SearchOutlined />} onClick={onQuery} loading={loading}>
          查詢
        </Button>
      </div>
    </div>
  );
};

export default QueryParams;
